import React, { useState, useEffect } from 'react';
import './RecapSelectionModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { formatFigure, lineNet } from '../../utils/precision';

/**
 * RecapSelectionModal Component
 * 
 * Modal for selecting which recaps (products) to include in an invoice/PO
 * when a delivery has multiple purchase or sales recaps.
 * Supports multi-selection with checkboxes.
 * Pre-selects recaps that match the selected partner.
 */
const RecapSelectionModal = ({ 
  isOpen, 
  onClose, 
  recapType, // 'purchase' or 'sales'
  recaps = [], // Available recaps from delivery
  selectedPartnerId = null, // CRM ID of selected partner for pre-selection
  onConfirm
}) => {
  const [selectedRecapIds, setSelectedRecapIds] = useState([]);
  const [selectedTotal, setSelectedTotal] = useState(0);

  // Pre-select recaps that match the selected partner when modal opens
  useEffect(() => {
    if (isOpen && recaps.length > 0) {
      console.log('[RecapSelectionModal] Recaps received:', recaps);
      console.log('[RecapSelectionModal] Recap type:', recapType);
      console.log('[RecapSelectionModal] Selected partner ID:', selectedPartnerId);
      
      if (selectedPartnerId) {
        // FILTER to only show recaps that match this partner
        const matchingRecapIds = recaps
          .filter(recap => {
            if (recapType === 'purchase') {
              // Match purchase recaps to seller
              return recap.purchase_seller_crm_id === selectedPartnerId;
            } else {
              // Match sales recaps to buyer
              return recap.sale_buyer_crm_id === selectedPartnerId;
            }
          })
          .map(recap => recap.id);
        
        console.log('[RecapSelectionModal] Matching recap IDs for partner:', matchingRecapIds);
        setSelectedRecapIds(matchingRecapIds);
        calculateTotal(matchingRecapIds, recaps);
      } else {
        // No partner selected - select all by default
        const allIds = recaps.map(r => r.id);
        console.log('[RecapSelectionModal] Selecting all recaps:', allIds);
        setSelectedRecapIds(allIds);
        calculateTotal(allIds, recaps);
      }
    }
    
    if (!isOpen) {
      setSelectedRecapIds([]);
      setSelectedTotal(0);
    }
  }, [isOpen, recaps, selectedPartnerId, recapType]);

  // Handle keyboard events
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && selectedRecapIds.length > 0) {
        handleConfirm();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedRecapIds, onClose]);

  // Calculate total amount for selected recaps
  const calculateTotal = (recapIds, allRecaps) => {
    const total = allRecaps
      .filter(recap => recapIds.includes(recap.id))
      .reduce((sum, recap) => {
        // Use allocated_quantity (effective quantity for this delivery)
        const qty = parseFloat(recap.allocated_quantity) || 0;
        const price = parseFloat(
          recapType === 'purchase' 
            ? recap.purchase_contract_price_value 
            : recap.sale_contract_price_value
        ) || 0;
        return sum + (qty * price);
      }, 0);
    
    setSelectedTotal(total);
  };

  const toggleRecap = (recapId) => {
    setSelectedRecapIds(prev => {
      const newIds = prev.includes(recapId)
        ? prev.filter(id => id !== recapId)
        : [...prev, recapId];
      
      calculateTotal(newIds, recaps);
      return newIds;
    });
  };

  const handleConfirm = () => {
    if (selectedRecapIds.length === 0) return;
    onConfirm(selectedRecapIds);
  };

  if (!isOpen) return null;

  const isPurchase = recapType === 'purchase';
  const title = isPurchase ? 'Select Products for Purchase Order' : 'Select Products for Invoice';
  const selectedCount = selectedRecapIds.length;
  
  // ISSUE 1 FIX: Filter recaps to only show those matching the selected partner
  const filteredRecaps = selectedPartnerId 
    ? recaps.filter(recap => {
        if (recapType === 'purchase') {
          return recap.purchase_seller_crm_id === selectedPartnerId;
        } else {
          return recap.sale_buyer_crm_id === selectedPartnerId;
        }
      })
    : recaps;
  
  console.log('[RecapSelectionModal] Filtered recaps for display:', filteredRecaps.length, 'out of', recaps.length);
  
  // Get currency from first filtered recap (all should have same currency)
  const currency = filteredRecaps.length > 0 
    ? (isPurchase 
        ? (filteredRecaps[0].purchase_contract_price_currency || 'USD')
        : (filteredRecaps[0].sale_contract_price_currency || 'USD'))
    : 'USD';

  return (
    <div className="recap-selection-modal-overlay" onClick={onClose}>
      <div className="recap-selection-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="recap-selection-modal-header">
          <h2>{title}</h2>
          {selectedCount > 0 && (
            <span className="recap-selection-selected-badge">
              {selectedCount} selected
            </span>
          )}
          <button 
            className="recap-selection-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="recap-selection-modal-body">
          <p className="recap-selection-description">
            Select which products should be included in this {isPurchase ? 'purchase order' : 'invoice'}:
          </p>

          {filteredRecaps.length === 0 ? (
            <div className="recap-selection-no-data">
              No recaps available for the selected partner
            </div>
          ) : (
            <div className="recap-selection-list">
              {filteredRecaps.map((recap) => {
                const isSelected = selectedRecapIds.includes(recap.id);
                
                // ISSUE 2 FIX: Use allocated_quantity (which is already the effective quantity from allocation)
                const quantity = recap.allocated_quantity;
                
                // ISSUE 2 FIX: Use friendly unit name from allocation
                const unitName = recap.allocated_quantity_unit_name || 
                                (isPurchase 
                                  ? recap.purchase_contract_quantity_unit_name 
                                  : recap.sale_contract_quantity_unit_name) || 
                                'units';
                
                const price = isPurchase 
                  ? recap.purchase_contract_price_value 
                  : recap.sale_contract_price_value;
                const recapCurrency = isPurchase 
                  ? recap.purchase_contract_price_currency 
                  : recap.sale_contract_price_currency;
                const partnerName = isPurchase
                  ? recap.purchase_seller_name || recap.purchase_seller
                  : recap.sale_buyer_name || recap.sale_buyer;
                
                // Parse values safely with fallbacks
                const quantityNum = parseFloat(quantity) || 0;
                const priceNum = parseFloat(price) || 0;
                const lineTotal = quantityNum * priceNum;
                
                // Format display values
                const quantityDisplay = quantity || '0';
                const unitDisplay = unitName || 'units';
                // Price shows every decimal it genuinely carries (at least 2),
                // read exactly off the string. The line total is the exact
                // product rounded to the cent -- what the invoice line will bill.
                const priceDisplay = priceNum > 0 ? formatFigure(price, 2) : '0.00';
                const currencyDisplay = recapCurrency || 'USD';
                const lineTotalDisplay = lineTotal > 0 ? lineNet(quantity, price) : '0.00';
                
                // Show partial indicator if this is a partial allocation
                const isPartial = recap.is_partial_allocation;

                return (
                  <div
                    key={recap.id}
                    className={`recap-selection-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleRecap(recap.id)}
                  >
                    <div className="recap-selection-checkbox">
                      <FontAwesomeIcon 
                        icon={isSelected ? faCheckSquare : faSquare}
                      />
                    </div>
                    
                    <div className="recap-selection-details">
                      <div className="recap-selection-row-1">
                        <span className="recap-selection-number">
                          {recap.display_name || recap.transaction_number}
                        </span>
                        {isPartial && (
                          <span className="recap-selection-partial-badge">
                            Partial
                          </span>
                        )}
                        {partnerName && (
                          <span className="recap-selection-partner">
                            {partnerName}
                          </span>
                        )}
                      </div>
                      
                      <div className="recap-selection-row-2">
                        <span className="recap-selection-product">
                          {recap.product}
                        </span>
                      </div>
                      
                      <div className="recap-selection-row-3">
                        <span className="recap-selection-quantity">
                          {quantityDisplay} {unitDisplay}
                        </span>
                        <span className="recap-selection-price">
                          @ {currencyDisplay} {priceDisplay}
                        </span>
                        <span className="recap-selection-total">
                          Total: {currencyDisplay} {lineTotalDisplay}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="recap-selection-modal-footer">
          <div className="recap-selection-footer-info">
            <span className="recap-selection-footer-label">Selected Total:</span>
            <span className="recap-selection-footer-amount">
              {currency} {(selectedTotal || 0).toFixed(2)}
            </span>
          </div>
          <div className="recap-selection-footer-actions">
            <button 
              className="recap-selection-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="recap-selection-confirm-btn"
              onClick={handleConfirm}
              disabled={selectedRecapIds.length === 0}
            >
              Continue with {selectedCount} Product{selectedCount !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecapSelectionModal;
